import type {
  CanonicalError,
  CanonicalStage,
  ChatSummary,
  ChatView,
  ModelRegistryView,
  ModelSummary,
  TimelineDelta,
  TimelineItem,
  WorkspaceView,
} from "../api/contracts";

export interface ConfigurationInvalidationScope {
  chatId: string | null;
  modelId: string;
}

export interface ModelRegistryReconciliation {
  workspace: WorkspaceView;
  changed: boolean;
  activeModelRemoved: boolean;
  readinessChanged: boolean;
}

const MODEL_WIDE_INVALIDATION_CODES = new Set([
  "dev_model_not_tagged",
  "dev_model_unavailable",
  "dev_model_binding_invalid",
]);

const MODEL_CHAT_SET_INVALIDATION_CODES = new Set([
  "dev_model_binding_changed",
  "chat_binding_stale",
]);

const CONFIGURATION_INVALIDATION_CODES = new Set([
  ...MODEL_WIDE_INVALIDATION_CODES,
  "chat_binding_stale",
]);

export function compareCursors(left: string, right: string): number {
  const leftValue = BigInt(left);
  const rightValue = BigInt(right);
  if (leftValue < rightValue) return -1;
  if (leftValue > rightValue) return 1;
  return 0;
}

export function normalizeWorkspace(workspace: WorkspaceView): WorkspaceView {
  const modelIds = new Set<string>();
  for (const model of workspace.models) {
    if (modelIds.has(model.modelId)) throw new Error("DUPLICATE_MODEL_ID");
    modelIds.add(model.modelId);
  }

  const chatsById = new Map<string, ChatSummary>();
  for (const chat of workspace.chats) {
    if (!modelIds.has(chat.modelId)) throw new Error("CHAT_MODEL_NOT_EXPOSED");
    if (chatsById.has(chat.chatId)) throw new Error("DUPLICATE_CHAT_ID");
    chatsById.set(chat.chatId, chat);
  }

  let chats = sortChats(workspace.chats);
  if (workspace.activeChat === null) {
    return { ...workspace, chats };
  }

  if (!modelIds.has(workspace.activeChat.modelId)) {
    throw new Error("ACTIVE_CHAT_MODEL_NOT_EXPOSED");
  }
  const activeSummary = chatsById.get(workspace.activeChat.chatId);
  if (
    activeSummary !== undefined &&
    !sameChatSummary(activeSummary, workspace.activeChat)
  ) {
    throw new Error("ACTIVE_CHAT_SUMMARY_MISMATCH");
  }
  if (activeSummary === undefined) {
    // `activeChat` is an explicitly requested resource and may legitimately
    // sit outside the bounded first history page. Keep it navigable without
    // weakening the server's page-size contract.
    chats = sortChats([chatSummaryFromView(workspace.activeChat), ...chats]);
  }

  const timeline = [...workspace.activeChat.timeline].sort((left, right) =>
    compareCursors(left.cursor, right.cursor),
  );
  const itemIds = new Set<string>();
  const cursors = new Set<string>();
  for (const item of timeline) {
    if (item.chatId !== workspace.activeChat.chatId) {
      throw new Error("TIMELINE_CHAT_SCOPE_MISMATCH");
    }
    if (compareCursors(item.cursor, workspace.cursor) > 0) {
      throw new Error("TIMELINE_CURSOR_AHEAD_OF_SNAPSHOT");
    }
    if (itemIds.has(item.itemId)) {
      throw new Error("DUPLICATE_TIMELINE_ITEM_ID");
    }
    if (cursors.has(item.cursor)) {
      throw new Error("DUPLICATE_TIMELINE_CURSOR");
    }
    itemIds.add(item.itemId);
    cursors.add(item.cursor);
  }

  return {
    ...workspace,
    chats,
    activeChat: { ...workspace.activeChat, timeline },
  };
}

export function reconcileModelRegistry(
  workspace: WorkspaceView,
  registry: ModelRegistryView,
): ModelRegistryReconciliation {
  const previousModels = new Map(
    workspace.models.map((model) => [model.modelId, model]),
  );
  const nextModels = new Map(
    registry.models.map((model) => [model.modelId, model]),
  );
  const readinessChanged = registry.models.some((model) => {
    const previous = previousModels.get(model.modelId);
    return previous !== undefined && !sameModelSummary(previous, model);
  });
  const changed =
    workspace.models.length !== registry.models.length ||
    workspace.models.some((model, index) => {
      const incoming = registry.models[index];
      return incoming === undefined || !sameModelSummary(model, incoming);
    });
  if (!changed) {
    return {
      workspace,
      changed: false,
      activeModelRemoved: false,
      readinessChanged: false,
    };
  }

  const chats = workspace.chats
    .filter((chat) => nextModels.has(chat.modelId))
    .map((chat) => reconcileChatReadiness(
      chat,
      previousModels.get(chat.modelId),
      nextModels.get(chat.modelId),
    ));
  const activeModelRemoved =
    workspace.activeChat !== null &&
    !nextModels.has(workspace.activeChat.modelId);
  const activeChat = activeModelRemoved || workspace.activeChat === null
    ? null
    : reconcileChatReadiness(
        workspace.activeChat,
        previousModels.get(workspace.activeChat.modelId),
        nextModels.get(workspace.activeChat.modelId),
      );
  const reconciled = normalizeWorkspace({
    ...workspace,
    models: registry.models,
    chats,
    activeChat,
    cursor: activeChat === null ? "0" : workspace.cursor,
  });
  return {
    workspace: reconciled,
    changed: true,
    activeModelRemoved,
    readinessChanged,
  };
}

export function mergeWorkspaceSnapshot(
  previous: WorkspaceView | null,
  incoming: WorkspaceView,
): WorkspaceView {
  const next = normalizeWorkspace(incoming);
  if (previous === null) return next;

  const allowedModels = new Set(next.models.map((model) => model.modelId));
  const chats = new Map<string, ChatSummary>();
  for (const chat of previous.chats) {
    if (allowedModels.has(chat.modelId)) chats.set(chat.chatId, chat);
  }
  for (const chat of next.chats) chats.set(chat.chatId, chat);

  let activeChat = next.activeChat;
  if (
    activeChat !== null &&
    previous.activeChat?.chatId === activeChat.chatId
  ) {
    activeChat = {
      ...activeChat,
      timeline: mergeHistoricalTimeline(
        previous.activeChat.timeline,
        activeChat.timeline,
      ),
      timelineNextBefore: previous.activeChat.timelineNextBefore,
    };
  }
  if (activeChat !== null && chats.has(activeChat.chatId)) {
    chats.set(activeChat.chatId, chatSummaryFromView(activeChat));
  }

  return normalizeWorkspace({
    ...next,
    chats: [...chats.values()],
    chatsNextBefore: previous.chatsNextBefore,
    activeChat,
  });
}

export function mergeChatPage(
  workspace: WorkspaceView,
  page: WorkspaceView,
): WorkspaceView {
  const incoming = normalizeWorkspace(page);
  if (incoming.activeChat !== null || incoming.cursor !== "0") {
    throw new Error("CHAT_PAGE_CONTAINED_ACTIVE_TIMELINE");
  }

  const allowedModels = new Set(incoming.models.map((model) => model.modelId));
  const chats = new Map<string, ChatSummary>();
  for (const chat of workspace.chats) {
    if (allowedModels.has(chat.modelId)) chats.set(chat.chatId, chat);
  }
  for (const chat of incoming.chats) chats.set(chat.chatId, chat);

  const activeChat =
    workspace.activeChat !== null && allowedModels.has(workspace.activeChat.modelId)
      ? workspace.activeChat
      : null;
  return normalizeWorkspace({
    ...workspace,
    models: incoming.models,
    chats: [...chats.values()],
    chatsNextBefore: incoming.chatsNextBefore,
    activeChat,
    cursor: activeChat === null ? "0" : workspace.cursor,
    csrfToken: incoming.csrfToken,
  });
}

export function mergeTimelinePage(
  workspace: WorkspaceView,
  page: WorkspaceView,
): WorkspaceView {
  const incoming = normalizeWorkspace(page);
  const currentChat = workspace.activeChat;
  const incomingChat = incoming.activeChat;
  if (
    currentChat === null ||
    incomingChat === null ||
    currentChat.chatId !== incomingChat.chatId ||
    currentChat.modelId !== incomingChat.modelId
  ) {
    throw new Error("TIMELINE_PAGE_CHAT_SCOPE_MISMATCH");
  }

  const timeline = mergeHistoricalTimeline(
    currentChat.timeline,
    incomingChat.timeline,
  );
  const updatedActive: typeof currentChat = {
    ...incomingChat,
    timeline,
    timelineNextBefore: incomingChat.timelineNextBefore,
  };
  const chats = workspace.chats.map((chat) =>
    chat.chatId === updatedActive.chatId
      ? chatSummaryFromView(updatedActive)
      : chat,
  );

  return normalizeWorkspace({
    ...workspace,
    models: incoming.models,
    chats,
    activeChat: updatedActive,
    csrfToken: incoming.csrfToken,
  });
}

function mergeHistoricalTimeline(
  current: TimelineItem[],
  incoming: TimelineItem[],
): TimelineItem[] {
  const byCursor = new Map(current.map((item) => [item.cursor, item]));
  const cursorByItemId = new Map(current.map((item) => [item.itemId, item.cursor]));
  for (const item of incoming) {
    const reusedCursor = cursorByItemId.get(item.itemId);
    if (reusedCursor !== undefined && reusedCursor !== item.cursor) {
      throw new Error("TIMELINE_ITEM_REUSED_WITH_NEW_CURSOR");
    }
    const existing = byCursor.get(item.cursor);
    if (existing !== undefined && existing.itemId !== item.itemId) {
      throw new Error("TIMELINE_REPLAY_MISMATCH");
    }
    byCursor.set(item.cursor, item);
    cursorByItemId.set(item.itemId, item.cursor);
  }
  return [...byCursor.values()].sort((left, right) =>
    compareCursors(left.cursor, right.cursor),
  );
}

export function mergeTimelineDelta(
  workspace: WorkspaceView,
  delta: TimelineDelta,
): WorkspaceView {
  const activeChat = workspace.activeChat;
  if (activeChat === null || delta.item.chatId !== activeChat.chatId) {
    throw new Error("TIMELINE_CHAT_SCOPE_MISMATCH");
  }
  if (delta.cursor !== delta.item.cursor) {
    throw new Error("TIMELINE_CURSOR_MISMATCH");
  }
  if (compareCursors(delta.cursor, workspace.cursor) <= 0) {
    const existing = activeChat.timeline.find((item) => item.cursor === delta.cursor);
    if (
      existing !== undefined &&
      existing.itemId === delta.item.itemId &&
      JSON.stringify(existing) === JSON.stringify(delta.item)
    ) {
      return workspace;
    }
    if (existing !== undefined && isRefreshableReplay(existing, delta.item)) {
      return {
        ...workspace,
        activeChat: {
          ...activeChat,
          timeline: activeChat.timeline.map((item) =>
            item.cursor === delta.cursor ? delta.item : item,
          ),
        },
      };
    }
    throw new Error("TIMELINE_REPLAY_MISMATCH");
  }
  if (activeChat.timeline.some((item) => item.itemId === delta.item.itemId)) {
    throw new Error("TIMELINE_ITEM_REUSED_WITH_NEW_CURSOR");
  }

  const updatedSummary = summaryAfterItem(activeChat, delta.item);
  return {
    ...workspace,
    cursor: delta.cursor,
    chats: sortChats([
      updatedSummary,
      ...workspace.chats.filter((chat) => chat.chatId !== activeChat.chatId),
    ]),
    activeChat: {
      ...updatedSummary,
      timeline: [...activeChat.timeline, delta.item],
      timelineNextBefore: activeChat.timelineNextBefore,
    },
  };
}

function isRefreshableReplay(existing: TimelineItem, incoming: TimelineItem): boolean {
  if (
    existing.kind !== incoming.kind ||
    existing.itemId !== incoming.itemId ||
    existing.chatId !== incoming.chatId ||
    existing.cursor !== incoming.cursor ||
    existing.timestamp !== incoming.timestamp
  ) return false;

  if (existing.kind === "media.v1" && incoming.kind === "media.v1") {
    return sameMediaPresentation(existing, incoming);
  }
  if (existing.kind === "offer.v1" && incoming.kind === "offer.v1") {
    return (
      existing.offerId === incoming.offerId &&
      existing.price === incoming.price &&
      (!existing.purchased || incoming.purchased) &&
      sameNullableMediaPresentation(existing.media, incoming.media)
    );
  }
  return false;
}

function sameNullableMediaPresentation(
  left: { mediaId: string; presentation: string; alt: string } | null,
  right: { mediaId: string; presentation: string; alt: string } | null,
): boolean {
  if (left === null || right === null) return left === right;
  return sameMediaPresentation(left, right);
}

function sameMediaPresentation(
  left: { mediaId: string; presentation: string; alt: string },
  right: { mediaId: string; presentation: string; alt: string },
): boolean {
  return (
    left.mediaId === right.mediaId &&
    left.presentation === right.presentation &&
    left.alt === right.alt
  );
}

export function latestCanonicalStatus(
  timeline: TimelineItem[],
): { stage: CanonicalStage; reasonCode: string | null; message: string | null } | null {
  for (let index = timeline.length - 1; index >= 0; index -= 1) {
    const item = timeline[index];
    if (item?.kind === "status.v1") {
      return {
        stage: item.stage,
        reasonCode: item.reasonCode,
        message: item.message,
      };
    }
  }
  return null;
}

export function isOfferPurchased(timeline: TimelineItem[], offerId: string): boolean {
  return timeline.some(
    (item) =>
      (item.kind === "offer.v1" && item.offerId === offerId && item.purchased) ||
      (item.kind === "purchase.v1" && item.offerId === offerId),
  );
}

export function chatsForModel(chats: ChatSummary[], modelId: string): ChatSummary[] {
  return chats.filter((chat) => chat.modelId === modelId);
}

export function isConfigurationInvalidationError(error: CanonicalError): boolean {
  return CONFIGURATION_INVALIDATION_CODES.has(error.code);
}

export function failClosedWorkspace(
  workspace: WorkspaceView,
  errorCode: string,
  scope: ConfigurationInvalidationScope,
): WorkspaceView {
  const modelWide = MODEL_WIDE_INVALIDATION_CODES.has(errorCode);
  const chats = workspace.chats.filter((chat) =>
    modelWide || MODEL_CHAT_SET_INVALIDATION_CODES.has(errorCode)
      ? chat.modelId !== scope.modelId
      : chat.chatId !== scope.chatId,
  );
  return normalizeWorkspace({
    ...workspace,
    models: modelWide
      ? workspace.models.filter((model) => model.modelId !== scope.modelId)
      : workspace.models,
    chats,
    activeChat: null,
    cursor: "0",
  });
}

export function failClosedAfterRefresh(
  workspace: WorkspaceView,
  errorCode: string,
  scope: ConfigurationInvalidationScope,
): WorkspaceView {
  if (MODEL_WIDE_INVALIDATION_CODES.has(errorCode)) {
    return workspace;
  }
  const modelChatSet = MODEL_CHAT_SET_INVALIDATION_CODES.has(errorCode);
  return normalizeWorkspace({
    ...workspace,
    chats: workspace.chats.filter((chat) =>
      modelChatSet
        ? chat.modelId !== scope.modelId
        : chat.chatId !== scope.chatId,
    ),
    activeChat:
      modelChatSet || workspace.activeChat?.chatId === scope.chatId
        ? null
        : workspace.activeChat,
  });
}

export function configurationScopeWasRemoved(
  workspace: WorkspaceView,
  errorCode: string,
  scope: ConfigurationInvalidationScope,
): boolean {
  if (MODEL_WIDE_INVALIDATION_CODES.has(errorCode)) {
    return !workspace.models.some((model) => model.modelId === scope.modelId);
  }
  if (MODEL_CHAT_SET_INVALIDATION_CODES.has(errorCode)) {
    return !workspace.chats.some((chat) => chat.modelId === scope.modelId);
  }
  return !workspace.chats.some((chat) => chat.chatId === scope.chatId);
}

function summaryAfterItem(chat: ChatSummary, item: TimelineItem): ChatSummary {
  return {
    ...chat,
    updatedAt: item.timestamp,
    lastItemAt: item.timestamp,
    lastItemPreview: previewForItem(item),
  };
}

function previewForItem(item: TimelineItem): string {
  switch (item.kind) {
    case "fan_message.v1":
    case "model_message.v1":
      return item.text;
    case "media.v1":
      return item.presentation === "photo" ? "Photo" : "Video preview";
    case "offer.v1":
      return item.purchased ? "Purchased offer" : `${item.currency} ${item.price} offer`;
    case "purchase.v1":
      return "Purchase confirmed";
    case "status.v1":
      return item.message ?? item.stage.replaceAll("_", " ");
  }
}

function sortChats(chats: ChatSummary[]): ChatSummary[] {
  return [...chats].sort((left, right) => {
    const createdOrder = Date.parse(right.createdAt) - Date.parse(left.createdAt);
    if (createdOrder !== 0) return createdOrder;
    return right.chatId.localeCompare(left.chatId);
  });
}

function chatSummaryFromView(chat: ChatView): ChatSummary {
  return {
    chatId: chat.chatId,
    fanId: chat.fanId,
    modelId: chat.modelId,
    createdAt: chat.createdAt,
    updatedAt: chat.updatedAt,
    lastItemPreview: chat.lastItemPreview,
    lastItemAt: chat.lastItemAt,
    readOnly: chat.readOnly,
    readinessReason: chat.readinessReason,
  };
}

function reconcileChatReadiness<T extends ChatSummary>(
  chat: T,
  previousModel: ModelSummary | undefined,
  nextModel: ModelSummary | undefined,
): T {
  if (previousModel === undefined || nextModel === undefined) {
    throw new Error("CHAT_MODEL_REGISTRY_STATE_MISSING");
  }
  if (sameModelSummary(previousModel, nextModel)) return chat;
  if (nextModel.readOnly) {
    return {
      ...chat,
      readOnly: true,
      readinessReason: nextModel.readinessReason,
    };
  }
  return {
    ...chat,
    readOnly: true,
    readinessReason: "readiness_refresh_required",
  };
}

function sameModelSummary(left: ModelSummary, right: ModelSummary): boolean {
  return (
    left.modelId === right.modelId &&
    left.readOnly === right.readOnly &&
    left.readinessReason === right.readinessReason
  );
}

function sameChatSummary(left: ChatSummary, right: ChatSummary): boolean {
  return (
    left.chatId === right.chatId &&
    left.fanId === right.fanId &&
    left.modelId === right.modelId &&
    left.createdAt === right.createdAt &&
    left.updatedAt === right.updatedAt &&
    left.lastItemPreview === right.lastItemPreview &&
    left.lastItemAt === right.lastItemAt
  );
}
